export class RcpStatus {
    static ENUM_VALUES: RcpStatus[] = [];
    static EMPTY = new RcpStatus("EMPTY");
    static MARKDOWN_NOT_VALID = new RcpStatus("MARKDOWN_NOT_VALID");
    static JSON_NOT_VALID = new RcpStatus("JSON_NOT_VALID");
    static MARKDOWN_FILE_MISSING = new RcpStatus("MARKDOWN_FILE_MISSING");
    static MARKDOWN_MISSING_INGREDIENTS = new RcpStatus("MARKDOWN_MISSING_INGREDIENTS");
    static JSON_MISSING = new RcpStatus("JSON_MISSING");
    static OK = new RcpStatus("OK");

    name: string;

    /**
     * Represents a recipe status
     * @param name {string} the name of the status
     */
    constructor(name: string) {
        this.name = name;
        RcpStatus.ENUM_VALUES.push(this);
    }

    /**
     * Convert a string to a RcpStatus
     * @param name {string} the string representation
     * @returns {RcpStatus} the RcpStatus
     */
    static fromString(name: string): RcpStatus {
        return RcpStatus.ENUM_VALUES.find(rs => rs.name === name) ?? RcpStatus.EMPTY;
    }
}

export class RecipeLevel {
    /**
     * @type {[RecipeLevel]}
     */
    static ENUM_VALUES: RecipeLevel[] = [];

    static BEGINNER = new RecipeLevel("BEGINNER");
    static INTERMEDIATE = new RecipeLevel( "INTERMEDIATE");
    static ADVANCED = new RecipeLevel( "ADVANCED");

    name: string;

    /**
     * Represents a recipe level
     * @param name {string} the name of the food type
     */
    constructor(name: string) {
        this.name = name;
        RecipeLevel.ENUM_VALUES.push(this);
    }

    /**
     * Convert a string to a RecipeLevel
     * @param str {string} the string representation
     * @returns {RecipeLevel} the RecipeLevel
     */
    static fromString(str: string): RecipeLevel {
        return RecipeLevel.ENUM_VALUES.find(rl => rl.name === str) ?? RecipeLevel.BEGINNER;
    }
}

export class RecipeIngredient {
    internalId: number;
    foodEntryId: number;
    portionSizeId: number;
    quantity: number;
    template: string;

    /**
     * An individual recipe ingredient
     * @param foodEntryId {number} the food entry
     * @param portionSizeId {number} an ID within the food entry or -1
     * @param quantity {number} the number of the above portions
     * @param template {string} the string used to render in live view
     * @param internalId {number} an internal ID that will not be serialized
     */
    constructor(foodEntryId: number, portionSizeId: number, quantity: number, template: string, internalId: number = 0) {
        this.internalId = internalId;
        this.foodEntryId = foodEntryId;
        this.portionSizeId = portionSizeId;
        this.quantity = quantity;
        this.template = template;
    }

    /**
     * Converts a JSON object into an instance of the class.
     *
     * @param {any} json - The JSON object containing the data to be converted.
     * @return {RecipeIngredient} An instance of the class populated with the data from the JSON object.
     */
    static fromJson(json: any): RecipeIngredient {
        return new RecipeIngredient(json.nutriId, json.measure, json.quantity, json.template);
    }

    /**
     * @returns {Object} JSON representation
     */
    toJson(): any {
        return {
            nutriId: this.foodEntryId,
            measure: this.portionSizeId,
            quantity: this.quantity,
            template: this.template
        }
    }
}

export class RecipeIngredientSet {
    internalId: number;
    name: string;
    ingredients: RecipeIngredient[];

    /**
     * A list of ingredients with a name
     * @param name {string} the name
     * @param ingredients {[RecipeIngredient]}
     * @param internalId {number} optional internal ID (not serialized)
     */
    constructor(name: string, ingredients: RecipeIngredient[], internalId: number = -1) {
        this.internalId = internalId;
        this.name = name;
        this.ingredients = ingredients;
    }

    static fromJson(obj: any): RecipeIngredientSet {
        return new RecipeIngredientSet(obj.name, obj.list.map(RecipeIngredient.fromJson));
    }

    toJson(): any {
        return {
            name: this.name,
            list: this.ingredients.map(ri => ri.toJson())
        }
    }
}

export class OutputFormat {
    static ENUM_VALUES: OutputFormat[] = [];
    static PNG = new OutputFormat("PNG");
    static JPG_LO = new OutputFormat("JPG_LO");
    static JPG_MED = new OutputFormat("JPG_MED");

    name: string;

    /**
     * construct an output format
     * @param name {string} the name matching the object entity
     */
    constructor(name: string) {
        this.name = name;
        OutputFormat.ENUM_VALUES.push(this);
    }

    static fromString(name: string): OutputFormat {
        return OutputFormat.ENUM_VALUES.find(of => of.name === name) ?? OutputFormat.JPG_LO;
    }
}

export class SourceImage {
    dir: string;
    srcImg: string;
    regWidth: number;
    maxWidth: number;
    outputFormat: OutputFormat;
    internalId: number;

    /**
     *
     * @param dir {string} - the directory containing the image
     * @param srcImg {string} - the source image
     * @param regWidth {number} the regular width
     * @param maxWidth {number} the maximum width
     * @param outputFormat {OutputFormat} the output format
     * @param internalId {number} internal ID not serialized
     */
    constructor(dir: string, srcImg: string, regWidth: number, maxWidth: number, outputFormat: OutputFormat, internalId: number = 0) {
        this.dir = dir;
        this.srcImg = srcImg;
        this.regWidth = regWidth;
        this.maxWidth = maxWidth;
        this.outputFormat = outputFormat;
        this.internalId = internalId;
    }

    toJson(): any {
        return {
            dir: this.dir,
            srcImg: this.srcImg,
            regWidthPx: this.regWidth,
            maxWidthPx: this.maxWidth,
            outputAs: this.outputFormat.name
        }
    }

    static fromJson(json: any): SourceImage {
        return new SourceImage(json.dir, json.srcImg, json.regWidthPx, json.maxWidthPx, OutputFormat.fromString(json.outputAs));
    }
}

export class RcpRecipe {
    static EMPTY_RECIPE = new RcpRecipe("00000000-0000-0000-0000-000000000000", "", RecipeLevel.BEGINNER, 0, 0, 0, new Date(), [], []);
    
    id: string;
    name: string;
    level: RecipeLevel;
    servings: number;
    totalTime: number;
    handsOnTime: number;
    lastUpdated: Date;
    ingredientSets: RecipeIngredientSet[];
    sourceImages: SourceImage[];

    /**
     * Represents a recipe with its details.
     *
     * @param {string} id - The unique identifier for the recipe.
     * @param {string} name - The name of the recipe.
     * @param {RecipeLevel} level - The difficulty level of the recipe (see Java class RecipeLevel for possible values).
     * @param {number} servings - The number of servings the recipe provides.
     * @param {number} totalTime - The total time required to prepare the recipe in minutes.
     * @param {number} handsOnTime - The active preparation time required in minutes.
     * @param {Date} lastUpdated - The date when the recipe was last updated.
     * @param {[RecipeIngredientSet]} ingredientSets - ingredient sets
     * @param {[SourceImage]} sourceImages - the source images
     */
    constructor(id: string, name: string, level: RecipeLevel, servings: number, totalTime: number, handsOnTime: number, lastUpdated: Date, ingredientSets: RecipeIngredientSet[], sourceImages: SourceImage[]) {
        this.id = id;
        this.name = name;
        this.level = level;
        this.servings = servings;
        this.totalTime = totalTime;
        this.handsOnTime = handsOnTime;
        this.lastUpdated = lastUpdated;
        this.ingredientSets = ingredientSets;
        this.sourceImages = sourceImages
    }

    /**
     * Converts the current object instance to a JSON representation.
     * @return {Object} A JSON object containing the serialized properties of the instance.
     */
    toJson(): any {
        return {
            recipe: {
                id: this.id,
                name: this.name,
                level: this.level.name,
                servings: this.servings,
                totalTime: this.totalTime,
                handsOnTime: this.handsOnTime,
                lastUpdated: this.lastUpdated,
                allIngredientLists: this.ingredientSets.map(is => is.toJson()),
            },
            sourceImages: this.sourceImages.map(is => is.toJson())
        }
    }

    /**
     * Converts a JSON object into an instance of the current class.
     * @param topJson {any} A JSON object containing the serialized properties of the instance.
     * @return {RcpRecipe} An instance of the current class.
     */
    static fromJson(topJson: any): RcpRecipe {
        let json = topJson.recipe;
        return new RcpRecipe(
            json.id, json.name, RecipeLevel.fromString(json.level), json.servings,
            json.totalTime, json.handsOnTime, new Date(json.lastUpdated),
            json.allIngredientLists.map(RecipeIngredientSet.fromJson),
            topJson.sourceImages.map(SourceImage.fromJson));
    }
}

export class RcpStaticRecipe {
    static EMPTY_STATIC_RECIPE = new RcpStaticRecipe("00000000-0000-0000-0000-000000000000", "/", "/", RcpStatus.EMPTY, null);

    uuid: string;
    jsonPath: string;
    markdownPath: string;
    status: RcpStatus;
    rcp: RcpRecipe | null;

    /**
     * Constructor for an rcp static recipe.
     *
     * @param uuid {string} the UUID of the recipe.
     * @param jsonPath {string} the json file name.
     * @param markdownPath {string} the markdown file name.
     * @param status {RcpStatus} the status of the recipe, see java class RcpRecipeStatus.
     * @param rcp {RcpRecipe | null} the recipe object.
     */
    constructor(uuid: string, jsonPath: string, markdownPath: string, status: RcpStatus, rcp: RcpRecipe | null = null) {
        this.uuid = uuid;
        this.jsonPath = jsonPath;
        this.markdownPath = markdownPath;
        this.status = status;
        this.rcp = rcp;
    }


    /**
     * Converts the current object instance to a JSON representation.
     * @return {Object} A JSON object containing the serialized properties of the instance.
     */
    toJson(): any {
        return {
            uuid: this.uuid,
            jsonPath: this.jsonPath,
            markdownPath: this.markdownPath,
            status: this.status.name,
            rcp: this.rcp?.toJson()
        };
    }

    /**
     * Converts a JSON object into an instance of the current class.
     * @param json {any} A JSON object containing the serialized properties of the instance.
     * @return {RcpStaticRecipe} An instance of the current class.
     */
    static fromJson(json: any): RcpStaticRecipe {
        return new RcpStaticRecipe(
            json.uuid, json.jsonPath, json.markdownPath, RcpStatus.fromString(json.status),
            json.rcp?.recipe ? RcpRecipe.fromJson(json.rcp) : null);
    }
}